import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { CalendarClock, GitBranch, Link2, Plug, Sparkles, type LucideIcon } from 'lucide-react'
import { Navigate } from '../components/nav'
import { useEmbedded } from '../lib/embed'
import { useGetProjectQuery, useGetProjectMembersQuery } from '../services/projectDataApi'
import ProjectGithubChatCard from '../components/chat/ProjectGithubChatCard'
import { isGithubConnected } from '../lib/githubTesting'
import '../components/chat/chat.css'
import SelfHealingSessions from '../components/SelfHealingSessions'
import ContinuousTestingSetup from '../components/ContinuousTestingSetup'
import AgentConnectionSetup from '../components/AgentConnectionSetup'
import IssueTrackerSetup from '../components/IssueTrackerSetup'
import SetupPageHeader, { type SetupBadge } from '../components/SetupPageHeader'
import { useProjectSetupStatus, type ProjectSetupStatus, type SetupItemKey } from '../components/useProjectSetupStatus'
import '../components/TestingSetup.css'
import './ProjectSupport.css'

const pages: Record<string, { title: string; icon: LucideIcon; description: string; item: SetupItemKey }> = {
  'self-healing': { title: 'Self Healing', icon: Sparkles, item: 'healing',
    description: 'Turn problems in real user sessions into root-caused bug reports.' },
  'test-schedule': { title: 'Test Schedule', icon: CalendarClock, item: 'schedule',
    description: 'Choose how often QA tests your app, how much it covers, and what it focuses on.' },
  'github': { title: 'GitHub', icon: GitBranch, item: 'github',
    description: 'Connect your repository so QA tests pull requests and main-branch deployments.' },
  'agent-connection': { title: 'Agent Connection', icon: Plug, item: 'agent',
    description: 'Hand the bugs QA finds straight to your coding agent, with full reports.' },
  'issue-tracker': { title: 'Issue Tracker', icon: Link2, item: 'tracker',
    description: 'Send new bug reports to the tracker your team already works in.' },
}

function badgeFor(item: SetupItemKey, { states, cards }: ProjectSetupStatus): SetupBadge | null {
  switch (states[item]) {
    case 'done': return item === 'agent' && cards.agent.tone !== 'white' ? { tone: 'neutral', label: 'Optional' } : { tone: 'success', label: 'Set up' }
    case 'waiting': return { tone: 'warning', label: 'Waiting for first session' }
    case 'off': return { tone: 'neutral', label: 'Off' }
    case 'todo': return item === 'github' && cards.github.tone === 'yellow' ? { tone: 'warning', label: 'Needs verification' } : { tone: 'neutral', label: 'Not set up' }
    default: return null
  }
}

export default function ProjectSupport() {
  const { projectId = '', support = '' } = useParams()
  const [githubOpen, setGithubOpen] = useState(false)
  const embedded = useEmbedded()
  const query = useGetProjectQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const members = useGetProjectMembersQuery(projectId)
  const setupStatus = useProjectSetupStatus(projectId)
  const role = members.currentData?.role
  const canEdit = role === 'owner' || role === 'admin' || role === 'member'
  const project = query.currentData
  if (support === 'security') return <Navigate to={`/projects/${projectId}/security`} replace />
  if (support === 'continuous-testing') return <Navigate to={`/projects/${projectId}/support/test-schedule`} replace />
  const page = Object.hasOwn(pages, support) ? pages[support] : undefined
  if (!page) return <Navigate to={`/projects/${projectId}/overview`} replace />
  const githubConnected = isGithubConnected(project?.github)
  return <div className="project-support setup-page" data-testid="project-support-page">
    <SetupPageHeader projectId={projectId} icon={page.icon} title={page.title} description={page.description} badge={badgeFor(page.item, setupStatus)} />
    <div className="project-support__content setup-page__body testing-settings">
    {query.isLoading && <p role="status">Loading project…</p>}
    {query.isError && <p role="alert">Could not load project. <button onClick={() => void query.refetch()}>Retry</button></p>}
    {project && <>
      {support === 'self-healing' && <SelfHealingSessions key={projectId} projectId={projectId} />}
      {support === 'test-schedule' && <ContinuousTestingSetup key={projectId} projectId={projectId} canEdit={canEdit} showDescription={false} />}
      {support === 'github' &&
        <section className="setup-section project-support__github" aria-labelledby="github-connection-title">
          <div className="setup-section__header">
            <div>
              <h2 id="github-connection-title">Repository</h2>
              <p>Choose which repository QA watches for new pull requests and deployments.</p>
            </div>
            {canEdit && <div className="setup-section__actions">
              {embedded ? <a href={`/projects/${projectId}/support/github`} target="_blank" rel="noopener noreferrer">Open GitHub setup in the full app</a>
                : <button type="button" onClick={() => setGithubOpen(true)}>{project.github?.repoUrl ? 'Manage GitHub connection' : 'Connect GitHub'}</button>}
            </div>}
          </div>
          <div className="setup-section__body">
            <p className={`setup-status${githubConnected ? ' setup-status--success' : ''}`}>
              <GitBranch size={16} aria-hidden />
              <strong>{githubConnected ? 'Connected' : project.github?.repoUrl ? 'Verify GitHub access' : 'Not connected'}</strong>
              {project.github?.repoUrl && <code className="setup-status__detail project-support__repository">{project.github.repoUrl}</code>}
            </p>
            {canEdit && githubOpen && <ProjectGithubChatCard key={projectId} project={project} onClose={() => setGithubOpen(false)} />}
          </div>
        </section>}
      {support === 'agent-connection' && <AgentConnectionSetup project={project} />}
      {support === 'issue-tracker' && <IssueTrackerSetup project={project} canEdit={canEdit} />}
    </>}
    </div>
  </div>
}
